import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase/client'
import { useEquipment, useInspection } from '@/hooks'
import { InspectionChecklist } from '@/components/inspection'
import { ClipboardCheck, FileText, CheckCircle, AlertTriangle } from 'lucide-react'
import { format } from 'date-fns'
import { nb } from 'date-fns/locale'
import { usePageHeader } from '@/components/layout/pageHeader'

const CHECKLIST_ITEMS = [
  { id: 'visual_damage',  label: 'Visuell inspeksjon for skader',            category: 'Visuell inspeksjon', required: true },
  { id: 'corrosion',      label: 'Kontroller for korrosjon',                 category: 'Visuell inspeksjon', required: true },
  { id: 'wear',           label: 'Kontroller for overdreven slitasje',       category: 'Visuell inspeksjon', required: true },
  { id: 'deformation',    label: 'Kontroller for deformasjon',               category: 'Visuell inspeksjon', required: true },
  { id: 'markings',       label: 'Verifiser at WLL-merking er leselig',      category: 'Dokumentasjon',      required: true },
  { id: 'certification',  label: 'Kontroller sertifiseringstagger',          category: 'Dokumentasjon',      required: true },
  { id: 'lubrication',    label: 'Kontroller smøring (hvis aktuelt)',        category: 'Vedlikehold',        required: false },
  { id: 'function_test',  label: 'Funksjonstest (hvis aktuelt)',             category: 'Testing',            required: false },
]

type Step = 'checklist' | 'notes' | 'review' | 'complete'

const STEPS: { key: Step; label: string; icon: React.ComponentType<{ size?: number; className?: string }> }[] = [
  { key: 'checklist', label: 'Sjekkliste',  icon: ClipboardCheck },
  { key: 'notes',     label: 'Merknader',   icon: FileText },
  { key: 'review',    label: 'Gjennomgå',   icon: CheckCircle },
]

const RESULT_LABEL: Record<string, string> = {
  pass: 'OK',
  fail: 'Avvik',
  na:   'N/A',
}

const RESULT_COLOR: Record<string, string> = {
  pass: 'text-emerald-600',
  fail: 'text-red-600',
  na:   'text-slate-400',
}

const InspectionFlow = () => {
  const { equipmentId } = useParams<{ equipmentId: string }>()
  const navigate = useNavigate()
  const { getEquipmentById } = useEquipment()
  const { submitInspection, loading: submitting } = useInspection()

  const [equipment, setEquipment] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [notes, setNotes] = useState('')
  const [checklistResults, setChecklistResults] = useState<Record<string, 'pass' | 'fail' | 'na'>>({})
  const [step, setStep] = useState<Step>('checklist')

  useEffect(() => {
    loadEquipment()
  }, [equipmentId])

  const loadEquipment = async () => {
    if (!equipmentId) return
    try {
      const data = await getEquipmentById(equipmentId)
      setEquipment(data)
    } catch {
      // handled in render
    } finally {
      setLoading(false)
    }
  }

  const handleChecklistComplete = (results: Record<string, 'pass' | 'fail' | 'na'>) => {
    setChecklistResults(results)
    setStep('notes')
  }

  const handleSubmit = async () => {
    if (!equipmentId) return
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('No user found')

      const hasFails = Object.values(checklistResults).some(r => r === 'fail')
      await submitInspection({
        equipment_id: equipmentId,
        inspector_id: user.id,
        inspection_date: new Date().toISOString(),
        status: hasFails ? 'fail' : 'pass',
        notes: notes || undefined,
      })
      setStep('complete')
    } catch {
      alert('Kunne ikke sende inn inspeksjon. Prøv igjen.')
    }
  }

  const handleBack = () => {
    if (step === 'notes')   return setStep('checklist')
    if (step === 'review')  return setStep('notes')
    navigate('/equipment')
  }

  const headerSubtitle = equipment
    ? [equipment.type, equipment.serial_number && `SN: ${equipment.serial_number}`, equipment.wll && `WLL: ${equipment.wll} kg`]
        .filter(Boolean)
        .join(' · ')
    : undefined

  usePageHeader(equipment?.name ?? 'Inspeksjon', {
    subtitle: headerSubtitle || undefined,
    showBack: step !== 'complete',
    onBack: handleBack,
  })

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="w-10 h-10 border-4 border-[#1E3A5F] border-t-transparent rounded-full animate-spin mb-3" />
        <span className="text-base text-slate-500">Laster inspeksjon...</span>
      </div>
    )
  }

  if (!equipment) {
    return (
      <div className="flex items-center justify-center h-full" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <p className="text-lg font-semibold text-red-600">Utstyr ikke funnet</p>
      </div>
    )
  }

  const passCount = Object.values(checklistResults).filter(r => r === 'pass').length
  const failCount = Object.values(checklistResults).filter(r => r === 'fail').length
  const stepIndex = STEPS.findIndex(s => s.key === step)

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ backgroundColor: 'var(--bg-primary)' }}>

      {/* ── Step indicator ────────────────────────────────────── */}
      {step !== 'complete' && (
        <div className="shrink-0 bg-white border-b border-slate-200 px-5 py-2 flex items-center gap-1">
          {STEPS.map((s, idx) => {
            const Icon = s.icon
            const done = idx < stepIndex
            const active = idx === stepIndex
            return (
              <div key={s.key} className="flex items-center gap-1">
                <div
                  className={[
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors',
                    active
                      ? 'bg-[#1E3A5F] text-white'
                      : done
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-slate-100 text-slate-400',
                  ].join(' ')}
                >
                  <Icon size={13} />
                  <span>{s.label}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`w-4 h-px ${done ? 'bg-emerald-300' : 'bg-slate-200'}`} />
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* ── Scrollable content ────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-5 py-5">

        {/* STEP 1 — Checklist */}
        {step === 'checklist' && (
          <InspectionChecklist
            items={CHECKLIST_ITEMS}
            onComplete={handleChecklistComplete}
          />
        )}

        {/* STEP 2 — Notes */}
        {step === 'notes' && (
          <div className="max-w-3xl mx-auto flex flex-col gap-5">
            <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-1">
                Steg 2 av 3
              </p>
              <h2 className="text-xl font-semibold text-slate-800 mb-1">Merknader</h2>
              <p className="text-sm text-slate-500 mb-4">
                Legg til observasjoner, avvik eller anbefalinger
              </p>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Skriv merknader her..."
                rows={8}
                className="w-full px-4 py-3 text-base border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/30 focus:border-[#1E3A5F] text-slate-800 bg-white resize-none transition-colors"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep('checklist')}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-white border border-slate-300 text-slate-700 text-base font-semibold rounded-md hover:bg-slate-50 transition-colors"
                style={{ minHeight: 52 }}
              >
                Tilbake
              </button>
              <button
                onClick={() => setStep('review')}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-[#1E3A5F] text-white text-base font-semibold rounded-md hover:bg-[#152C4A] transition-colors shadow-sm"
                style={{ minHeight: 52 }}
              >
                Gjennomgå og send
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 — Review */}
        {step === 'review' && (
          <div className="max-w-3xl mx-auto flex flex-col gap-4">
            {/* Summary row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 text-center shadow-sm">
                <p className="text-2xl font-bold text-emerald-600">{passCount}</p>
                <p className="text-xs text-slate-500 font-medium mt-0.5">OK</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 text-center shadow-sm">
                <p className={`text-2xl font-bold ${failCount > 0 ? 'text-red-600' : 'text-slate-300'}`}>{failCount}</p>
                <p className="text-xs text-slate-500 font-medium mt-0.5">Avvik</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 text-center shadow-sm">
                <p className="text-2xl font-bold text-slate-400">
                  {Object.values(checklistResults).filter(r => r === 'na').length}
                </p>
                <p className="text-xs text-slate-500 font-medium mt-0.5">N/A</p>
              </div>
            </div>

            {/* Details card */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
              {/* Equipment row */}
              <div className="px-5 py-4 border-b border-slate-100">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-1">Utstyr</p>
                <p className="text-base font-semibold text-slate-800">{equipment.name}</p>
                {equipment.serial_number && (
                  <p className="text-sm text-slate-500">SN: {equipment.serial_number}</p>
                )}
              </div>

              {/* Date row */}
              <div className="px-5 py-4 border-b border-slate-100">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-1">Inspeksjonsdato</p>
                <p className="text-base text-slate-700">{format(new Date(), 'd. MMMM yyyy', { locale: nb })}</p>
              </div>

              {/* Results */}
              <div className="px-5 py-4 border-b border-slate-100">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Resultater</p>
                <div className="divide-y divide-slate-100">
                  {CHECKLIST_ITEMS.map(item => (
                    <div key={item.id} className="flex items-center justify-between py-2.5">
                      <span className="text-sm text-slate-700 flex-1 min-w-0 pr-4 truncate">{item.label}</span>
                      <span className={`text-sm font-semibold shrink-0 ${RESULT_COLOR[checklistResults[item.id] ?? 'na']}`}>
                        {RESULT_LABEL[checklistResults[item.id] ?? 'na']}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              {notes && (
                <div className="px-5 py-4">
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">Merknader</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{notes}</p>
                </div>
              )}
            </div>

            {/* Warning if there are fails */}
            {failCount > 0 && (
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 font-medium">
                  {failCount} avvik registrert. Inspeksjonen vil bli markert som ikke godkjent.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setStep('notes')}
                disabled={submitting}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-white border border-slate-300 text-slate-700 text-base font-semibold rounded-md hover:bg-slate-50 transition-colors disabled:opacity-50"
                style={{ minHeight: 52 }}
              >
                Tilbake
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className={`flex-1 flex items-center justify-center gap-2 px-5 py-3 text-white text-base font-semibold rounded-md transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                  failCount > 0 ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
                style={{ minHeight: 52 }}
              >
                {submitting ? 'Sender inn...' : 'Send inn inspeksjon'}
              </button>
            </div>
          </div>
        )}

        {/* STEP 4 — Complete */}
        {step === 'complete' && (
          <div className="max-w-xl mx-auto flex flex-col items-center gap-5 pt-8">
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center shadow-sm ${
                failCount > 0 ? 'bg-amber-100' : 'bg-emerald-100'
              }`}
            >
              <CheckCircle
                size={36}
                className={failCount > 0 ? 'text-amber-600' : 'text-emerald-600'}
              />
            </div>

            <div className="text-center">
              <h2 className="text-2xl font-semibold text-slate-800 mb-1">
                Inspeksjon fullført
              </h2>
              <p className="text-base text-slate-500">
                Inspeksjonen er lagret og registrert.
              </p>
            </div>

            {/* Outcome badge */}
            <div className={`flex items-center gap-2 px-5 py-3 rounded-lg border ${
              failCount > 0
                ? 'bg-amber-50 border-amber-200 text-amber-800'
                : 'bg-emerald-50 border-emerald-200 text-emerald-800'
            }`}>
              <span className="text-base font-semibold">
                {failCount > 0
                  ? `${failCount} avvik registrert — ikke godkjent`
                  : 'Alle punkter godkjent'}
              </span>
            </div>

            <button
              onClick={() => navigate('/equipment')}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#1E3A5F] text-white text-base font-semibold rounded-md hover:bg-[#152C4A] transition-colors shadow-sm"
              style={{ minHeight: 52 }}
            >
              Tilbake til utstyrsliste
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default InspectionFlow
